import { X } from 'lucide-react'
import type {
  FinanceAssignee,
  FinanceRequestDetail,
} from '@/services/financeService'
import {
  portalModalOverlay,
} from '@/components/PortalPj/portalPjUi'
import { cn } from '@/utils/cn'
import { FinanceRequestDetailContent } from './FinanceRequestDetailContent'
import { FinanceRequestDetailHeader } from './FinanceRequestDetailHeader'
import { FinanceRequestDetailBody } from './FinanceRequestDetailBody'

type Props = {
  open: boolean
  loading: boolean
  detail: FinanceRequestDetail | null
  canManageQueue: boolean
  canViewFinancialValues?: boolean
  canViewClosedScope?: boolean
  assignees: FinanceAssignee[]
  presentation?: 'drawer' | 'portal'
  onClose: () => void
  onUpdateStatus: (status: string, approvalPendingFrom?: string) => Promise<boolean>
  onAssign: (assigneeId: string | null) => Promise<boolean>
  onAddComment: (body: string, isInternal?: boolean, attachments?: string[]) => Promise<boolean>
  onReload?: () => void
}

export function FinanceRequestDetailPanel({
  open,
  loading,
  detail,
  canManageQueue,
  canViewFinancialValues = true,
  canViewClosedScope = true,
  assignees,
  presentation = 'drawer',
  onClose,
  onUpdateStatus,
  onAssign,
  onAddComment,
  onReload,
}: Props) {
  const isPortal = presentation === 'portal'

  if (!open) return null

  const content = loading || !detail ? (
    <div className='flex justify-center py-16'>
      <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
    </div>
  ) : isPortal ? (
    <div className='space-y-6'>
      <FinanceRequestDetailHeader detail={detail} />
      <FinanceRequestDetailContent
        detail={detail}
        canManageQueue={canManageQueue}
        canViewFinancialValues={canViewFinancialValues}
        canViewClosedScope={canViewClosedScope}
        assignees={assignees}
        onUpdateStatus={onUpdateStatus}
        onAssign={onAssign}
        onAddComment={onAddComment}
        onReload={onReload}
      />
    </div>
  ) : (
    <FinanceRequestDetailBody
      detail={detail}
      canManageQueue={canManageQueue}
      assignees={assignees}
      layout='drawer'
      onUpdateStatus={onUpdateStatus}
      onAssign={onAssign}
      onAddComment={onAddComment}
    />
  )

  if (isPortal) {
    return (
      <div
        className={portalModalOverlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <div
          role='dialog'
          aria-modal='true'
          aria-label={detail?.typeLabel ?? 'Detalhe do chamado'}
          className={cn(
            'flex max-h-[min(94vh,900px)] w-full flex-col overflow-hidden rounded-t-2xl border border-border/60 bg-bg-primary shadow-2xl sm:max-w-[min(96vw,1200px)] sm:rounded-2xl',
          )}
        >
          <div className='flex shrink-0 items-center justify-end border-b border-border/70 px-4 py-3 sm:px-5'>
            <button
              type='button'
              onClick={onClose}
              className='rounded-xl p-2 text-text-muted transition-colors hover:bg-bg-secondary'
              aria-label='Fechar'
            >
              <X size={18} />
            </button>
          </div>
          <div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 sm:px-6 sm:pb-6'>
            {content}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='fixed inset-0 z-[100] flex justify-end bg-black/40'>
      <div className='flex h-full min-w-0 w-full max-w-lg flex-col overflow-hidden border-l border-border bg-bg-primary shadow-xl'>
        <div className='flex items-center justify-between border-b border-border px-5 py-4'>
          <div>
            <p className='font-mono text-xs text-text-muted'>{detail?.protocol ?? '…'}</p>
            <h2 className='text-lg font-semibold text-text-primary'>
              {detail?.typeLabel ?? 'Carregando…'}
            </h2>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='rounded-md p-1 text-text-muted hover:bg-bg-secondary'
            aria-label='Fechar'
          >
            <X size={18} />
          </button>
        </div>
        <div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-4'>{content}</div>
      </div>
    </div>
  )
}
